import { PDFDocument } from 'pdf-lib';
import { Template } from '@pdfme/generator';
import { getFont } from '../utils';
import { generatePdfUint8Array } from './pdfUtilities';

const ITEM_PER_PAGE_FIRST = 18;
const ITEM_PER_PAGE_NEXT = 36;

export const outsourcePdf = async (data: any) => {
  // 初回読み込みの容量を減らすため動的インポート
  const base_pdf_top_with_total = await import(
    '../assets/outsource_base_pdf_top_with_total'
  ); //1枚で完結
  const base_pdf_top_no_total = await import(
    '../assets/outsource_base_pdf_top_no_total'
  ); //2枚以上あるときの1枚目
  const base_pdf_mid = await import('../assets/outsource_base_pdf_mid'); //中間ページ
  const base_pdf_last = await import('../assets/outsource_base_pdf_last'); //最終ページ
  const font = await getFont();
  const mergedPdf = await PDFDocument.create();

  // 1ページで完結する場合
  if (data.items.length <= ITEM_PER_PAGE_FIRST) {
    // 1ページで完結する場合は、1ページ目に基本情報と備考・小計欄をつける
    let schemas = { ...createOutsourceInfoSchema(), ...createSummarySchema() };
    let inputs = {
      ...createOutsourceInfoInput(data),
      ...createSummaryInput(data)
    };

    const page: any = await generatePage(
      data,
      font,
      0,
      data.items.length,
      146.3,
      schemas,
      inputs,
      base_pdf_top_with_total.base_pdf_top_with_total
    );

    return new Blob([page.buffer], { type: 'application/pdf' });
  }

  // 複数ページ必要な場合

  // 項目数から、必要なページ数を計算
  const remainingItems = data.items.length - ITEM_PER_PAGE_FIRST;
  const totalPages = Math.ceil(remainingItems / ITEM_PER_PAGE_NEXT) + 1;

  // ページの最初の項目と最後の項目のインデックス
  let startIdx = 0;
  let endIdx = ITEM_PER_PAGE_FIRST;

  for (let pageIndex = 1; pageIndex <= totalPages; pageIndex++) {
    // ページを生成するためのプロパティ
    let schemas, inputs, offsetY, basePdf;

    // 1ページ目には商品発注書基本情報をつける
    if (pageIndex === 1) {
      schemas = createOutsourceInfoSchema();
      inputs = createOutsourceInfoInput(data);
      offsetY = 146.3;
      basePdf = base_pdf_top_no_total.base_pdf_top_no_total;
    } else if (pageIndex === totalPages) {
      // 最終ページには備考・小計欄をつける
      schemas = createSummarySchema();
      inputs = createSummaryInput(data);
      offsetY = 19.5;
      basePdf = base_pdf_last.base_pdf_last;
    } else {
      schemas = {};
      inputs = {};
      offsetY = 19.5;
      basePdf = base_pdf_mid.base_pdf_mid;
    }

    // ページを生成
    const page = await generatePage(
      data,
      font,
      startIdx,
      endIdx,
      offsetY,
      schemas,
      inputs,
      basePdf
    );

    await mergePdfPage(page, mergedPdf);

    // ページの最初の項目と最後の項目のインデックスを更新
    startIdx = endIdx;
    endIdx = Math.min(data.items.length, endIdx + ITEM_PER_PAGE_NEXT);
  }

  //PDFの出力
  const exportPdf = await mergedPdf.save();
  return new Blob([exportPdf.buffer], { type: 'application/pdf' });
};

const mergePdfPage = async (pageX: any, mergedPdf: any) => {
  const pdfDoc = await PDFDocument.load(pageX);

  const copiedPage = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
  copiedPage.forEach((page: any) => mergedPdf.addPage(page));

  return pdfDoc;
};

const generatePage = async (
  data: any,
  font: any,
  startIdx: number,
  endIdx: number,
  offsetY: number,
  schemas: any,
  inputs: any,
  basePdf: any
) => {
  //明細列の出力
  for (let idx = startIdx; idx < endIdx; idx++) {
    const item = data.items[idx];
    // ページ内でのインデックス
    let idxInPage = idx - startIdx;

    // 各工事内容のスキーマと入力データを作成
    addItemRow(
      schemas,
      inputs,
      item,
      idxInPage,
      idx,
      offsetY + idxInPage * 7.05
    );
  }

  const template: Template = {
    basePdf: basePdf,
    schemas: [schemas]
  };

  const pdf = await generatePdfUint8Array(template, [inputs], font);
  return pdf;
};

/**
 * 各項目を表すスキーマと入力データを、
 * PDF出力のためのスキーマオブジェクトと入力データオブジェクトに追加する
 * @param schemas PDF出力のためのスキーマオブジェクト
 * @param inputs PDF出力のための入力データオブジェクト
 * @param item 項目データ
 * @param indexInPage ページ内でのインデックス
 * @param itemIndex 全項目内でのインデックス
 * @param yPos y方向開始位置
 */
const addItemRow = (
  schemas: any,
  inputs: any,
  item: any,
  indexInPage: number,
  itemIndex: number,
  yPos: number
) => {
  const commonSchema = {
    type: 'text',
    height: 5,
    verticalAlignment: 'middle'
  };

  schemas[`number${indexInPage}`] = {
    // 通番
    position: {
      x: 7.2,
      y: yPos
    },
    width: 17.27,
    alignment: 'center',
    fontSize: 10,
    ...commonSchema
  };

  schemas[`construction_details${indexInPage}`] = {
    // 工事内容
    position: {
      x: 23,
      y: yPos
    },
    width: 109,
    alignment: 'left',
    fontSize: 10,
    ...commonSchema
  };

  if (!item.hidden_flg) {
    schemas[`quantity${indexInPage}`] = {
      // 数量
      position: {
        x: 131,
        y: yPos
      },
      width: 17.27,
      alignment: 'center',
      dynamicFontSize: {
        max: 10,
        min: 8.2
      },
      ...commonSchema
    };

    schemas[`unit_price${indexInPage}`] = {
      // 単価
      position: {
        x: 121,
        y: yPos
      },
      width: 50,
      alignment: 'right',
      fontSize: 10,
      ...commonSchema
    };

    schemas[`order_amount${indexInPage}`] = {
      // 発注金額
      position: {
        x: 147,
        y: yPos
      },
      width: 50,
      alignment: 'right',
      fontSize: 10,
      ...commonSchema
    };
  }

  inputs[`number${indexInPage}`] = String(itemIndex + 1);
  if (window.location.pathname.includes('/received')) {
    inputs[`construction_details${indexInPage}`] =
      item.outsource_detail_construction_name;
    inputs[`quantity${indexInPage}`] = item.outsource_detail_quantity;
    inputs[`unit_price${indexInPage}`] = item.outsource_detail_unit_price;
    inputs[`order_amount${indexInPage}`] = (
      item.outsource_detail_quantity * item.outsource_detail_unit_price
    ).toLocaleString('ja-JP');
  } else {
    inputs[`construction_details${indexInPage}`] = item.construction_name;
    inputs[`quantity${indexInPage}`] =
      item.quantity.toLocaleString('ja-JP') + item.unit_name;
    inputs[`unit_price${indexInPage}`] =
      item.unit_price.toLocaleString('ja-JP');
    inputs[`order_amount${indexInPage}`] = item.amount.toLocaleString('ja-JP');
  }
};

/**
 * 外注発注書基本情報のテンプレートに入力されるデータを作成する
 * @param data 元データ
 * @returns 外注発注書基本情報のテンプレートに入力されるデータ
 */
const createOutsourceInfoSchema = () => {
  return {
    companyStamp: {
      // 会社印
      type: 'image',
      position: {
        x: 179.79,
        y: 12.61
      },
      width: 15,
      height: 15
    },
    supplier_name: {
      // 宛先名
      type: 'text',
      position: {
        x: 8.73,
        y: 15.8
      },
      width: 105.38,
      height: 9.38,
      alignment: 'center',
      fontSize: 12,
      characterSpacing: 0,
      lineHeight: 2.5
    },
    order_date: {
      // 発注日
      type: 'text',
      position: {
        x: 149,
        y: 9.1
      },
      width: 35,
      height: 5,
      alignment: 'left',
      fontSize: 10,
      characterSpacing: 0,
      lineHeight: 1
    },
    billing_note: {
      // 発注注意事項
      type: 'text',
      position: {
        x: 12.39,
        y: 30.23
      },
      width: 100.79,
      height: 19.76,
      alignment: 'left',
      fontSize: 9,
      characterSpacing: 0,
      lineHeight: 1
    },
    construction_number: {
      // 工事番号
      type: 'text',
      position: {
        x: 35.45,
        y: 57.7
      },
      width: 35,
      height: 5,
      alignment: 'center',
      fontSize: 12,
      characterSpacing: 0,
      lineHeight: 1
    },
    construction_site_name: {
      // 現場名
      type: 'text',
      position: {
        x: 97,
        y: 57.7
      },
      width: 100,
      height: 5,
      alignment: 'left',
      dynamicFontSize: {
        max: 10,
        min: 6
      }
    },
    site_address: {
      // 現場住所
      type: 'text',
      position: {
        x: 35.19,
        y: 66.3
      },
      width: 149.56,
      height: 5.26,
      alignment: 'left',
      fontSize: 10,
      characterSpacing: 0,
      lineHeight: 1
    },
    start_date: {
      // 工事開始日
      type: 'text',
      position: {
        x: 31.23,
        y: 79.2
      },
      width: 72.03,
      height: 4.99,
      alignment: 'center',
      fontSize: 11,
      characterSpacing: 0,
      lineHeight: 1
    },
    end_date: {
      // 工事終了日
      type: 'text',
      position: {
        x: 126.21,
        y: 79.2
      },
      width: 71.77,
      height: 4.99,
      alignment: 'center',
      fontSize: 11,
      characterSpacing: 0,
      lineHeight: 1
    },
    payment_due_date: {
      // 支払予定日
      type: 'text',
      position: {
        x: 51,
        y: 90.3
      },
      width: 35,
      height: 5,
      alignment: 'left',
      fontSize: 11,
      characterSpacing: 0,
      lineHeight: 1
    },
    invoice_amount: {
      // 精算金
      type: 'text',
      position: {
        x: 136.52,
        y: 90.3
      },
      width: 45.58,
      height: 5,
      alignment: 'right',
      fontSize: 11,
      characterSpacing: 0,
      lineHeight: 1
    },
    company_name: {
      // 会社名
      type: 'text',
      position: {
        x: 139,
        y: 13.4
      },
      width: 63,
      height: 5.97,
      alignment: 'left',
      fontSize: 11,
      characterSpacing: 0,
      lineHeight: 1.5
    },
    company_name_long: {
      // 会社名の長さで、フィールドの位置を切り替える
      type: 'text',
      position: {
        x: 139,
        y: 12.4
      },
      width: 63,
      height: 5,
      alignment: 'left',
      fontSize: 10,
      characterSpacing: 0,
      lineHeight: 1.2
    },
    company_info: {
      // 会社情報
      type: 'text',
      position: {
        x: 139,
        y: 21
      },
      width: 65,
      height: 34.81,
      alignment: 'left',
      fontSize: 9,
      characterSpacing: 0,
      lineHeight: 1
    },
    vendor_name: {
      // 宛先会社名
      type: 'text',
      position: {
        x: 97.5,
        y: 109
      },
      width: 90,
      height: 5,
      alignment: 'left',
      fontSize: 11,
      characterSpacing: 0,
      lineHeight: 1
    },
    vendor_representative: {
      // 宛先代表者氏名
      type: 'text',
      position: {
        x: 97.5,
        y: 116.6
      },
      width: 90,
      height: 5,
      alignment: 'left',
      fontSize: 11,
      characterSpacing: 0,
      lineHeight: 1
    },
    vendor_supervisor: {
      // 宛先担当者氏名
      type: 'text',
      position: {
        x: 97.5,
        y: 125.6
      },
      width: 90,
      height: 5,
      alignment: 'left',
      fontSize: 11,
      characterSpacing: 0,
      lineHeight: 1
    }
  };
};

/**
 * 外注発注書基本情報のテンプレートに入力されるデータを作成する
 * @param data 元データ
 * @returns 外注発注書基本情報のテンプレートに入力されるデータ
 */
const createOutsourceInfoInput = (data: any) => {
  // 会社名が長い場合フラグを持たせる
  let long_company_name_flg = data.company_name.length > 14;

  return {
    // 会社印
    companyStamp:
      data.company_stamp && data.company_stamp !== ''
        ? `data:${data.company_stamp_format};base64,${data.company_stamp}`
        : '',
    // 宛先名
    supplier_name: `${data.supplier_name ? data.supplier_name : ''} ${
      data.honorific_title ? data.honorific_title : ''
    }`,
    // 発注日
    order_date: window.location.pathname.includes('/received')
      ? data.order_date
      : data.order_date_str,
    // 発注注意事項
    billing_note: data.billing_note,
    // 工事番号
    construction_number: data.construction_number,
    // 現場名
    construction_site_name: data.construction_name,
    // 現場住所
    site_address: data.site_address,
    // 工事開始日
    start_date: window.location.pathname.includes('/received')
      ? data.job_start_date
      : data.job_start_date_str,
    // 工事終了日
    end_date: window.location.pathname.includes('/received')
      ? data.job_end_date
      : data.job_end_date_str,
    // 支払い予定日
    payment_due_date: window.location.pathname.includes('/received')
      ? data.payment_date
      : data.payment_date_str,
    // 精算金
    invoice_amount: data.total_price.toLocaleString('ja-JP'),
    // 会社名
    company_name: long_company_name_flg ? '' : data.company_name,
    // 会社名の長さで、フィールドの位置を切り替える
    company_name_long: long_company_name_flg ? data.company_name : '',
    // 会社住所
    company_info: `〒${data.company_postal_code.slice(
      0,
      3
    )} - ${data.company_postal_code.slice(-4)}
${data.company_address}

TEL: ${data.company_phone_number}
FAX: ${data.company_fax_number}

担当者: ${data.supervisor_name ? data.supervisor_name : ''}
`,
    // 宛先会社名
    vendor_name: data.supplier_name,
    // 宛先代表者氏名
    vendor_representative: data.vendor_representative,
    // 宛先担当者氏名
    vendor_supervisor: data.vendor_supervisor
  };
};

/**
 * 合計欄のスキーマを作成する
 * @returns 合計欄のスキーマ
 */
const createSummarySchema = () => {
  return {
    total_amount: {
      // 合計金額
      type: 'text',
      position: {
        x: 146,
        y: 272.5
      },
      width: 51,
      height: 5,
      alignment: 'right',
      fontSize: 10,
      characterSpacing: 0,
      lineHeight: 1
    },
    tax_rate_label: {
      // 消費税率
      type: 'text',
      position: {
        x: 140,
        y: 278.5
      },
      width: 35,
      height: 4.35,
      alignment: 'left',
      fontSize: 11
    },
    consumption_tax: {
      // 消費税
      type: 'text',
      position: {
        x: 146,
        y: 278.8
      },
      width: 51,
      height: 5,
      alignment: 'right',
      fontSize: 10,
      characterSpacing: 0,
      lineHeight: 1
    },
    net_amount: {
      // 税込合計金額
      type: 'text',
      position: {
        x: 146,
        y: 285.3
      },
      width: 51,
      height: 5,
      alignment: 'right',
      fontSize: 10,
      characterSpacing: 0,
      lineHeight: 1
    }
  };
};

/**
 * 合計欄の、テンプレートに入力されるデータを作成する
 * @param data 元データ
 * @returns 合計欄の、テンプレートに入力されるデータ
 */
const createSummaryInput = (data: any) => {
  return {
    total_amount: data.billing_total.toLocaleString('ja-JP'),
    tax_rate_label: `(${String(data.tax_rate * 100)}%)`,
    consumption_tax: data.tax.toLocaleString('ja-JP'),
    net_amount: data.total_price.toLocaleString('ja-JP')
  };
};
